import Banner from "./components/Banner";
import General from "./components/General";
import Note from "./components/Note";
import QuotationBuilderModal from "./components/QuotationBuilderModal";
// import Project from "./components/Project";
import Upload from "./components/Upload";
import Details from "./components/Details";
import Schedule from "./components/Schedule";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useState, useEffect } from "react";
import { IoCheckmark, IoArrowBack } from "react-icons/io5";
import { usePermission } from "hooks/usePermission";
import { updateLead, getLead, addLeadInstallment } from "../tables/leadApi";
import InstallmentTable from "components/Installments/InstallmentTable";
import { buildApiUrl } from "../../../utils/api";
import { getToken } from "../../../utils/auth";

const ProfileOverview = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const { can } = usePermission();

  // Get lead ID from URL params or location state
  const leadIdFromState = location.state?.lead?.id || location.state?.lead?._id || location.state?.leadId;
  const leadIdFromParams = params.leadId;
  const leadId = leadIdFromParams || leadIdFromState;
  const leadFromState = location.state?.lead;
  const [currentLead, setCurrentLead] = useState(null);
  const [fetching, setFetching] = useState(!!leadId);
  const [loading, setLoading] = useState(false);
  const [isQuotationOpen, setIsQuotationOpen] = useState(false);

  const lead = currentLead || leadFromState;
  const canUpdateLead = can("lead_management", "update");

  const [toast, setToast] = useState({ show: false, message: "", type: "" });

  const showToast = (message, type) => {
    setToast({ show: true, message, type });
  };

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: "", type: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [toast.show]);

  // Fetch lead details from API
  useEffect(() => {
    const fetchLeadDetails = async () => {
      if (!leadId) {
        setFetching(false);
        return;
      }

      setFetching(true);
      try {
        const response = await getLead(leadId);
        const leadData = response?.data || response;
        setCurrentLead(leadData);
      } catch (error) {
        console.error("Error fetching lead details:", error);
        showToast(
          error?.response?.data?.message || error.message || "Failed to fetch lead details",
          "error"
        );
        // Fall back to location state if API fails
        if (location.state?.lead) {
          setCurrentLead(location.state?.lead);
        }
      } finally {
        setFetching(false);
      }
    };

    fetchLeadDetails();
  }, [leadId, location.state?.lead]);

  const handleAddInstallment = async (installmentData) => {
    try {
      const result = await addLeadInstallment(lead._id, installmentData);
      if (result.success) {
        showToast("Installment added successfully", "success");
        // Refetch lead details to get updated installments and receivedAmount
        const response = await getLead(leadId);
        setCurrentLead(response?.data || response);
      } else {
        showToast(result.message || "Failed to add installment", "error");
      }
    } catch (error) {
      showToast(error.message || "Failed to add installment", "error");
    }
  };

  return (
    <div className="flex w-full flex-col gap-5">
      {fetching && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-brand-500"></div>
            <p className="mt-3 text-sm text-gray-600 dark:text-gray-400">Loading lead details...</p>
          </div>
        </div>
      )}

      {!fetching && !lead && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <p className="text-lg text-red-600 dark:text-red-400">Failed to load lead details</p>
            <button
              onClick={() => navigate("/admin/lead-management")}
              className="mt-4 inline-flex items-center gap-2 rounded-2xl bg-brand-500 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-600"
            >
              <IoArrowBack className="text-base" />
              Back to Management
            </button>
          </div>
        </div>
      )}

      {!fetching && lead && (
        <>
          {toast.show && (
            <div
              className={`fixed right-6 top-20 z-[60] rounded-lg px-4 py-3 text-sm font-semibold shadow-md backdrop-blur-sm ${toast.type === "error"
                ? "border border-red-400/70 bg-red-500/20 text-red-700"
                : "border border-green-400/70 bg-green-500/20 text-green-700"
                }`}
            >
              {toast.message}
            </div>
          )}

          <QuotationBuilderModal
            isOpen={isQuotationOpen}
            onClose={() => setIsQuotationOpen(false)}
            lead={lead}
            showToast={showToast}
            onSaved={(quotation) => {
              console.log("Quotation saved", quotation);
            }}
          />

          {/* Header with back and convert buttons */}
          <div className="mt-5 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate(location.state?.from || "/admin/lead-management")}
                className="flex items-center text-navy-700 hover:text-brand-500 dark:text-white dark:hover:text-brand-500"
              >
                <IoArrowBack className="text-2xl font-bold" />
              </button>
              <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
                Lead Profile
              </h1>
            </div>
            <div className="flex items-center gap-3">
              {(can("quotation", "create") || can("quotation", "view") || can("quotation", "download")) && (
                <button
                  onClick={() => setIsQuotationOpen(true)}
                  className="rounded-full bg-emerald-500 px-4 py-2 text-white transition-colors hover:bg-emerald-600"
                >
                  Create Quotation
                </button>
              )}
             
              {canUpdateLead && (
                <button
                  onClick={async () => {
                    if (!lead?._id) return;
                    setLoading(true);
                    try {
                      const token = getToken();
                      const res = await fetch(buildApiUrl("/leads/change-status"), {
                        method: "PUT",
                        headers: {
                          "Content-Type": "application/json",
                          Authorization: token ? `Bearer ${token}` : undefined,
                        },
                        body: JSON.stringify({ leadId: lead._id, newStatus: "Converted" })
                      });
                      if (!res.ok) throw new Error("Failed to convert lead");
                      showToast("Lead converted to client!", "success");
                      setTimeout(() => {
                        // Always set back target to client list so Back goes to client management
                        navigate(`/admin/client-management/client-profile/${lead._id}`, { state: { from: "/admin/client-management", client: { ...lead, _id: lead._id } } });
                      }, 500);
                    } catch (e) {
                      showToast("Failed to convert lead", "error");
                    } finally {
                      setLoading(false);
                    }
                  }}
                  className="flex items-center gap-2 rounded-full bg-blue-500 px-4 py-2 text-white transition-colors hover:bg-blue-600"
                  disabled={loading}
                >
                  <IoCheckmark className="text-lg" />
                  {loading ? "Converting..." : "Convert"}
                </button>
              )}
            </div>
          </div>

          <div className="w-full mt-3 flex h-fit flex-col gap-5 lg:grid lg:grid-cols-12">
            <div className="col-span-7 lg:!mb-0">
              <Banner
                lead={lead}
                showToast={showToast}
                setCurrentLead={setCurrentLead}
              />
            </div>

            <div className="z-0 col-span-5 lg:!mb-0">
              <Upload lead={lead} />
            </div>
          </div>

          {/* Additional Details Card - Full Width */}
          {/* <div className="w-full lg:!mb-0">
        <Details
          lead={lead}
          isEditing={isEditing}
          editedData={editedData}
          setEditedData={setEditedData}
          fieldErrors={fieldErrors}
        />
      </div> */}

          {/* all project & ... */}

          <div className="grid h-full grid-cols-1 gap-5 lg:!grid-cols-12">
            <div className="col-span-5 lg:col-span-6 lg:mb-0 3xl:col-span-4">
              {/* <Project lead={lead} isEditing={isEditing} /> */}
              <Details
                lead={lead}
                updateLead={updateLead}
                showToast={showToast}
                setCurrentLead={setCurrentLead}
              />
            </div>
            <div className="col-span-5 lg:col-span-6 lg:mb-0 3xl:col-span-5">
              <General
                lead={lead}
                updateLead={updateLead}
                showToast={showToast}
                setCurrentLead={setCurrentLead}
              />
            </div>

            <div className="col-span-5 lg:col-span-12 lg:mb-0 3xl:!col-span-3">
              <Note lead={lead} />
            </div>

            <div className="col-span-5 lg:col-span-12 lg:mb-0">
              <Schedule
                lead={lead}
                updateLead={updateLead}
                showToast={showToast}
                setCurrentLead={setCurrentLead}
              />
            </div>

            <div className="col-span-5 lg:col-span-12 lg:mb-0">
              <InstallmentTable
                installments={lead?.installments}
                onAddInstallment={handleAddInstallment}
                canUpdate={canUpdateLead}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ProfileOverview;
